import { ReactNode, useCallback, useMemo } from 'react';
import { Box, Drawer, Theme, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from '../../style/theme';

type Props = {
  open: boolean;
  drawerContent: ReactNode;
  children: ReactNode;
};

export const DynamicDrawerLayout = ({ open, drawerContent, children }: Props) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), { noSsr: true });
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'), { noSsr: true });

  const getDrawerWidth = useCallback(() => {
    if (isDesktop) {
      return theme.breakpoints.values.md;
    }
    if (isTablet) {
      return theme.breakpoints.values.sm;
    }
    return '100%';
  }, [isDesktop, isTablet]);

  const drawerWidth = getDrawerWidth();

  const Main = useMemo(
    () =>
      styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
        open?: boolean;
      }>(({ theme, open }) => ({
        flexGrow: 1,
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        marginRight: drawerWidth === '100%' ? 0 : -drawerWidth,
        ...(open && {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginRight: 0,
        }),
      })),
    [drawerWidth],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <Main open={open}>{children}</Main>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
          },
        }}
        variant={isDesktop ? 'persistent' : 'temporary'}
        anchor="right"
        open={open}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};
